import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { ClockIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useGenerationMetrics } from "../../../useQuery/useGenerationMetrics";

/**
 * How long it took to generate an endpoint's test cases: start, finish, duration and count.
 *
 * Timing only. LLM cost is intentionally not shown — the backend derives it from a single
 * hardcoded rate pair that does not vary by model and omits reasoning tokens.
 */

const fmtDuration = (ms) => {
  if (ms == null) return "—";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}m ${rem}s`;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const Metric = ({ label, value, title, tone = "default" }) => (
  <Tooltip title={title || ""} arrow disableHoverListener={!title}>
    <div className="flex flex-col min-w-0">
      <span className="text-[10px] uppercase tracking-wider text-gray-400 font-apple whitespace-nowrap">
        {label}
      </span>
      <span
        className={`text-[13px] font-semibold tabular-nums truncate ${
          tone === "muted" ? "text-gray-500" : "text-gray-900"
        }`}
      >
        {value}
      </span>
    </div>
  </Tooltip>
);

const StatusDot = ({ status }) => {
  const ok = status === "SUCCESS";
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${
        ok ? "bg-green-500" : status === "FAILED" ? "bg-red-500" : "bg-gray-300"
      }`}
      title={status || "unknown"}
    />
  );
};

const RunRow = ({ run }) => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5 border-t border-gray-100">
    <div className="flex items-center gap-2 min-w-0">
      <StatusDot status={run.status} />
      <span className="text-xs text-gray-500 font-apple truncate">
        {fmtTime(run.startTime)} &rarr; {fmtTime(run.endTime)}
      </span>
    </div>
    <Metric label="Duration" value={fmtDuration(run.durationMs)} />
    <Metric label="Cases" value={run.testCasesGenerated ?? "—"} />
    {run.msPerTestCase != null && (
      <Metric label="Per case" value={fmtDuration(run.msPerTestCase)} tone="muted" />
    )}
    {run.createdBy && <Metric label="Run by" value={run.createdBy} tone="muted" />}
    {run.status === "FAILED" && run.errorMessage && (
      <span className="text-xs text-red-700 font-apple basis-full break-words">
        {run.errorMessage}
      </span>
    )}
  </div>
);

const GenerationMetricsStrip = ({ endpointId }) => {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, isError } = useGenerationMetrics(endpointId);

  if (!endpointId) return null;

  if (isLoading) {
    return (
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs text-gray-400 font-apple">
        Loading generation metrics…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs text-gray-500 font-apple">
        Generation metrics unavailable.
      </div>
    );
  }

  const runs = data?.runs || [];
  const latest = data?.latest;
  const totals = data?.totals;

  if (!latest) {
    return (
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs text-gray-500 font-apple">
        No generation run recorded for this endpoint
        {totals?.currentTestCaseCount
          ? ` — its ${totals.currentTestCaseCount} test cases predate run tracking.`
          : "."}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-b border-gray-200">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-shrink-0">
          <ClockIcon className="h-4 w-4 text-gray-400" />
          <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold font-apple whitespace-nowrap">
            Last generation
          </span>
          <StatusDot status={latest.status} />
        </div>

        <Metric label="Started" value={fmtTime(latest.startTime)} />
        <Metric label="Finished" value={fmtTime(latest.endTime)} />
        <Metric label="Duration" value={fmtDuration(latest.durationMs)} />
        <Metric
          label="Cases"
          value={
            latest.testCasesGenerated != null
              ? latest.testCasesGenerated
              : totals?.currentTestCaseCount ?? "—"
          }
          title="Test cases this run added"
        />
        {latest.msPerTestCase != null && (
          <Metric label="Per case" value={fmtDuration(latest.msPerTestCase)} tone="muted" />
        )}

        {runs.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="ml-auto flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-apple cursor-pointer flex-shrink-0"
          >
            {runs.length} runs
            {totals?.durationMs != null && (
              <span className="text-gray-400">&middot; {fmtDuration(totals.durationMs)} total</span>
            )}
            <ChevronDownIcon
              className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      {expanded && runs.length > 1 && (
        <div className="bg-white">
          {runs.map((run) => (
            <RunRow key={run.runId} run={run} />
          ))}
        </div>
      )}
    </div>
  );
};

export default GenerationMetricsStrip;
